import React, { useEffect } from "react";
import { Helmet } from "react-helmet";

const styles = {
  page: {
    fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif",
    background: "#0F0F0F",
    color: "#FFFFFF",
    margin: 0,
    padding: 0,
    overflowX: "hidden",
  },
  hero: {
    position: "relative",
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    padding: "0 6vw",
    background: "linear-gradient(135deg, #0F0F0F 0%, #1A1A1A 60%, #120820 100%)",
    borderBottom: "1px solid #2a2a2a",
    overflow: "hidden",
  },
  heroAccent: {
    position: "absolute",
    top: "-120px",
    right: "-80px",
    width: "480px",
    height: "480px",
    background: "radial-gradient(circle, rgba(123,25,188,0.18) 0%, transparent 70%)",
    pointerEvents: "none",
  },
  heroBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    background: "rgba(123,25,188,0.12)",
    border: "1px solid rgba(123,25,188,0.35)",
    borderRadius: "100px",
    padding: "6px 16px",
    fontSize: "12px",
    fontWeight: 600,
    letterSpacing: "0.08em",
    color: "#7b19bc",
    textTransform: "uppercase",
    marginBottom: "28px",
    width: "fit-content",
  },
  heroDot: {
    width: "6px",
    height: "6px",
    borderRadius: "50%",
    background: "#7b19bc",
    animation: "pulse 2s infinite",
  },
  heroTitle: {
    fontSize: "clamp(2.8rem, 6vw, 5.5rem)",
    fontWeight: 900,
    lineHeight: 1.02,
    letterSpacing: "-0.03em",
    margin: "0 0 12px 0",
    maxWidth: "820px",
  },
  heroOrange: {
    color: "#7b19bc",
  },
  heroSub: {
    fontSize: "clamp(1rem, 1.8vw, 1.2rem)",
    color: "#ccc",
    maxWidth: "520px",
    lineHeight: 1.65,
    margin: "20px 0 0 0",
  },
  heroMeta: {
    display: "flex",
    gap: "40px",
    marginTop: "52px",
    flexWrap: "wrap",
  },
  heroMetaItem: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  heroMetaLabel: {
    fontSize: "11px",
    fontWeight: 600,
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    color: "#555",
  },
  heroMetaValue: {
    fontSize: "15px",
    fontWeight: 500,
    color: "#ccc",
  },
  sectionDark: {
    padding: "100px 6vw",
    background: "#0F0F0F",
    color: "#FFFFFF",
  },
  sectionLight: {
    padding: "100px 6vw",
    background: "#FFFFFF",
    borderTop: "1px solid #e5e5e5",
    borderBottom: "1px solid #e5e5e5",
  },
  eyebrow: {
    fontSize: "11px",
    fontWeight: 700,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    color: "#7b19bc",
    marginBottom: "18px",
  },
  h2: {
    fontSize: "clamp(1.8rem, 3.5vw, 3rem)",
    fontWeight: 800,
    letterSpacing: "-0.025em",
    lineHeight: 1.1,
    margin: "0 0 24px 0",
    maxWidth: "680px",
    color: "#FFFFFF",
  },
  body: {
    fontSize: "1.05rem",
    color: "#bbb",
    lineHeight: 1.8,
    maxWidth: "620px",
  },
  eyebrowDark: {
    fontSize: "11px",
    fontWeight: 700,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    color: "#7b19bc",
    marginBottom: "18px",
  },
  h2Dark: {
    fontSize: "clamp(1.8rem, 3.5vw, 3rem)",
    fontWeight: 800,
    letterSpacing: "-0.025em",
    lineHeight: 1.1,
    margin: "0 0 24px 0",
    maxWidth: "680px",
    color: "#111",
  },
  problemGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "0",
    marginTop: "60px",
    border: "1px solid #2a2a2a",
    borderRadius: "4px",
    overflow: "hidden",
  },
  problemCard: {
    padding: "40px",
    borderRight: "1px solid #2a2a2a",
    background: "#161616",
  },
  problemCardRight: {
    padding: "40px",
    background: "#161616",
  },
  problemCardTitleBad: {
    fontSize: "12px",
    fontWeight: 700,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    marginBottom: "20px",
    color: "#9b1515",
  },
  problemCardTitleGood: {
    fontSize: "12px",
    fontWeight: 700,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    marginBottom: "20px",
    color: "#22aa55",
  },
  problemItem: {
    display: "flex",
    gap: "12px",
    alignItems: "flex-start",
    marginBottom: "16px",
    fontSize: "14px",
    color: "#aaa",
    lineHeight: 1.5,
  },
  iconBad: { color: "#9b1515", fontWeight: 700, flexShrink: 0, marginTop: "1px" },
  iconGood: { color: "#22aa55", fontWeight: 700, flexShrink: 0, marginTop: "1px" },
  resultsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: "20px",
    marginTop: "56px",
  },
  resultCard: {
    background: "#f9f9f9",
    border: "1px solid #e5e5e5",
    borderRadius: "4px",
    padding: "36px",
  },
  resultCardHighlight: {
    background: "rgba(123,25,188,0.06)",
    border: "1px solid rgba(123,25,188,0.25)",
    borderRadius: "4px",
    padding: "36px",
  },
  resultNum: {
    fontSize: "3rem",
    fontWeight: 900,
    letterSpacing: "-0.04em",
    color: "#7b19bc",
    lineHeight: 1,
    marginBottom: "8px",
  },
  resultLabel: {
    fontSize: "14px",
    fontWeight: 600,
    color: "#111",
    marginBottom: "8px",
  },
  resultDesc: {
    fontSize: "13px",
    color: "#555",
    lineHeight: 1.55,
  },
  testimonialWrap: {
    background: "#0F0F0F",
    borderTop: "1px solid #222",
    borderBottom: "1px solid #222",
    padding: "60px 6vw",
  },
  quoteIcon: {
    fontSize: "64px",
    lineHeight: 1,
    color: "#7b19bc",
    fontFamily: "Georgia, serif",
    marginBottom: "16px",
    display: "block",
  },
  quoteText: {
    fontSize: "clamp(1.2rem, 2.2vw, 1.6rem)",
    fontWeight: 500,
    lineHeight: 1.65,
    color: "#ddd",
    maxWidth: "780px",
    fontStyle: "italic",
    marginBottom: "36px",
  },
  quoteAuthor: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
  },
  quoteAvatar: {
    width: "48px",
    height: "48px",
    borderRadius: "50%",
    background: "linear-gradient(135deg, #7b19bc, #4a0d8a)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "18px",
    fontWeight: 800,
    color: "#fff",
    flexShrink: 0,
  },
  quoteAuthorName: {
    fontSize: "15px",
    fontWeight: 700,
    color: "#e0e0e0",
  },
  quoteAuthorTitle: {
    fontSize: "13px",
    color: "#555",
    marginTop: "2px",
  },
  ctaSection: {
    padding: "100px 6vw",
    textAlign: "center",
    background: "#0F0F0F",
    borderTop: "1px solid #1e1e1e",
  },
  ctaSub: {
    fontSize: "1.05rem",
    color: "#666",
    marginBottom: "40px",
    maxWidth: "480px",
    margin: "0 auto 40px auto",
    lineHeight: 1.65,
  },
  ctaBtn: {
    display: "inline-block",
    background: "#7b19bc",
    color: "#fff",
    padding: "16px 40px",
    borderRadius: "4px",
    fontSize: "15px",
    fontWeight: 700,
    textDecoration: "none",
    letterSpacing: "0.02em",
    border: "none",
    cursor: "pointer",
    transition: "background 0.2s, transform 0.15s",
  },
  footerStrip: {
    borderTop: "1px solid #1a1a1a",
    padding: "28px 6vw",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    fontSize: "12px",
    color: "#444",
  },
};

// JSON-LD structured data
const structuredData = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Article",
      "@id": "https://www.reflexadvisor.com/torontojunkdisposalcasestudy#article",
      "headline": "From Zero Leads to Fully Booked in 60 Days — Toronto Junk Disposal Case Study",
      "description": "How Reflex Advisor rebuilt Toronto Junk Disposal's entire digital presence — website, automation, and sales pipeline — and turned silence into a steady stream of paying customers.",
      "image": "https://pub-b515366b2bdd4fb696b694c901df278b.r2.dev/images/junkremoval.png",
      "author": {
        "@type": "Organization",
        "name": "Reflex Advisor",
        "url": "https://www.reflexadvisor.com"
      },
      "publisher": {
        "@type": "Organization",
        "name": "Reflex Advisor",
        "url": "https://www.reflexadvisor.com",
        "logo": {
          "@type": "ImageObject",
          "url": "https://www.reflexadvisor.com/logo.png"
        }
      },
      "datePublished": "2024-01-01",
      "dateModified": "2025-01-01",
      "mainEntityOfPage": "https://www.reflexadvisor.com/torontojunkdisposalcasestudy"
    },
    {
      "@type": "Organization",
      "@id": "https://www.reflexadvisor.com#organization",
      "name": "Reflex Advisor",
      "url": "https://www.reflexadvisor.com",
      "description": "Reflex Advisor delivers done-for-you lead generation and sales automation for service-based businesses in Canada.",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Toronto",
        "addressRegion": "Ontario",
        "addressCountry": "CA"
      },
      "sameAs": [
        "https://ariusbusiness.com/company/reflex-advisor"
      ]
    },
    {
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": "https://www.reflexadvisor.com"
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Success Stories",
          "item": "https://www.reflexadvisor.com/successstories"
        },
        {
          "@type": "ListItem",
          "position": 3,
          "name": "Toronto Junk Disposal Case Study",
          "item": "https://www.reflexadvisor.com/torontojunkdisposalcasestudy"
        }
      ]
    }
  ]
};

export default function TorontoJunkDisposalCaseStudy() {

  useEffect(() => {
    const style = document.createElement("style");
    style.innerHTML = `
      @keyframes pulse {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.3; }
      }
      @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  return (
    <div style={styles.page}>

      <Helmet>
        {/* Primary */}
        <title>Toronto Junk Disposal Case Study | Reflex Advisor — 40+ Leads in 60 Days</title>
        <meta name="description" content="See how Reflex Advisor took Toronto Junk Disposal from zero online presence to 40+ inbound leads in 60 days with a new website, CRM automation, and local SEO." />
        <meta name="keywords" content="Reflex Advisor case study, Toronto junk removal marketing, lead generation junk removal, service business growth Canada, CRM automation Toronto, local SEO junk removal" />
        <link rel="canonical" href="https://www.reflexadvisor.com/torontojunkdisposalcasestudy" />

        {/* Open Graph */}
        <meta property="og:type" content="article" />
        <meta property="og:title" content="Toronto Junk Disposal Case Study | Reflex Advisor — 40+ Leads in 60 Days" />
        <meta property="og:description" content="How Reflex Advisor rebuilt Toronto Junk Disposal's entire digital presence — website, automation, and sales pipeline — turning silence into a steady stream of paying customers." />
        <meta property="og:url" content="https://www.reflexadvisor.com/torontojunkdisposalcasestudy" />
        <meta property="og:image" content="https://pub-b515366b2bdd4fb696b694c901df278b.r2.dev/images/junkremoval.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="Reflex Advisor" />
        <meta property="og:locale" content="en_CA" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Toronto Junk Disposal Case Study | Reflex Advisor" />
        <meta name="twitter:description" content="40+ inbound leads in 60 days. See how Reflex Advisor built the full growth system for Toronto Junk Disposal." />
        <meta name="twitter:image" content="https://pub-b515366b2bdd4fb696b694c901df278b.r2.dev/images/junkremoval.png" />

        {/* Indexing */}
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
        <meta name="author" content="Reflex Advisor" />

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      {/* ── Hero (dark) ── */}
      <section style={styles.hero} aria-label="Case study overview">
        <div style={styles.heroAccent} />
        <div style={styles.heroBadge}>
          <span style={styles.heroDot} />
          Case Study — Junk Removal · Toronto, ON
        </div>
        <h1 style={styles.heroTitle}>
          From Zero Leads to{" "}
          <span style={styles.heroOrange}>Fully Booked</span>{" "}
          in 60 Days
        </h1>
        <p style={styles.heroSub}>
          How Reflex Advisor rebuilt Toronto Junk Disposal's entire digital
          presence — website, automation, and sales pipeline — and turned
          silence into a steady stream of paying customers.
        </p>
        <div style={styles.heroMeta}>
          {[
            { label: "Client", value: "Toronto Junk Disposal" },
            { label: "Location", value: "Toronto, Ontario, Canada" },
            { label: "Timeline", value: "60 Days" },
            { label: "Website", value: "torontjunkdisposal.online" },
          ].map((m) => (
            <div key={m.label} style={styles.heroMetaItem}>
              <span style={styles.heroMetaLabel}>{m.label}</span>
              <span style={styles.heroMetaValue}>{m.value}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ── Problem (dark) ── */}
      <section style={styles.sectionDark} aria-label="The problem before Reflex Advisor">
        <div style={styles.eyebrow}>The Problem</div>
        <h2 style={styles.h2}>
          A great service. A completely invisible business.
        </h2>
        <p style={styles.body}>
          Toronto Junk Disposal had the trucks, the team, and the work ethic —
          but no digital footprint. Every potential customer who Googled "junk
          removal Toronto" found their competitors instead. Leads that did come
          in through word-of-mouth were handled manually, fell through cracks,
          and rarely converted.
        </p>

        <div style={styles.problemGrid}>
          <div style={styles.problemCard}>
            <div style={styles.problemCardTitleBad}>Before Reflex</div>
            {[
              "No website — zero online presence whatsoever",
              "Leads tracked in a WhatsApp group and paper notes",
              "Quote requests took 24–48 hours to get a reply",
              "No way to track which jobs were booked or pending",
              "Competitors dominated every local search result",
              "No follow-up system — cold leads went cold forever",
            ].map((item) => (
              <div key={item} style={styles.problemItem}>
                <span style={styles.iconBad}>✕</span>
                <span>{item}</span>
              </div>
            ))}
          </div>
          <div style={styles.problemCardRight}>
            <div style={styles.problemCardTitleGood}>After Reflex</div>
            {[
              "Professional website converting visitors to quote requests",
              "CRM pipeline tracking every lead from inquiry to booked job",
              "Automated responses sent within minutes of a new enquiry",
              "Live dashboard showing booked, pending, and completed jobs",
              "Local SEO setup targeting Toronto neighbourhoods directly",
              "Automated follow-up sequences re-engaging cold leads",
            ].map((item) => (
              <div key={item} style={styles.problemItem}>
                <span style={styles.iconGood}>✓</span>
                <span>{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── What We Built (light) ── */}
      <section style={styles.sectionLight} aria-label="What Reflex Advisor built for Toronto Junk Disposal">
        <div style={styles.eyebrowDark}>What We Built</div>
        <h2 style={styles.h2Dark}>
          A complete growth engine. Not just a website.
        </h2>

        <div style={{ marginTop: "56px", display: "flex", flexDirection: "column", alignItems: "center", gap: "0" }}>
          {[
            { icon: "🌐", title: "High-Converting Website", desc: "Mobile-first site with quote form & local service pages" },
            { icon: "📍", title: "Local SEO Foundation", desc: "Google Business Profile + neighbourhood landing pages" },
            { icon: "⚡", title: "Instant Response Automation", desc: "Personalised reply sent within 60 seconds of enquiry" },
            { icon: "🤖", title: "CRM & Lead Pipeline", desc: "New Lead → Quoted → Booked → Completed, automatically" },
            { icon: "📨", title: "Follow-Up Sequences", desc: "5-step cadence re-engages cold leads over 14 days" },
            { icon: "📊", title: "Sales Dashboard", desc: "One screen: all jobs, pipeline revenue & lead volume" },
          ].map((s, i) => (
            <div key={s.title} style={{ display: "flex", flexDirection: "column", alignItems: "center", width: "100%" }}>
              <div style={{
                display: "grid",
                gridTemplateColumns: "1fr auto 1fr",
                alignItems: "center",
                width: "100%",
                maxWidth: "760px",
              }}>
                {i % 2 === 0 ? (
                  <>
                    <div style={{ textAlign: "right", paddingRight: "32px" }}>
                      <div style={{ fontSize: "13px", fontWeight: 700, color: "#111", marginBottom: "4px" }}>{s.title}</div>
                      <div style={{ fontSize: "12px", color: "#666", lineHeight: 1.5 }}>{s.desc}</div>
                    </div>
                    <div style={{
                      width: "52px", height: "52px", borderRadius: "50%",
                      background: "rgba(123,25,188,0.08)", border: "1.5px solid rgba(123,25,188,0.4)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: "22px", flexShrink: 0, zIndex: 1,
                    }}>{s.icon}</div>
                    <div style={{ paddingLeft: "32px" }} />
                  </>
                ) : (
                  <>
                    <div style={{ paddingRight: "32px" }} />
                    <div style={{
                      width: "52px", height: "52px", borderRadius: "50%",
                      background: "rgba(123,25,188,0.08)", border: "1.5px solid rgba(123,25,188,0.4)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: "22px", flexShrink: 0, zIndex: 1,
                    }}>{s.icon}</div>
                    <div style={{ textAlign: "left", paddingLeft: "32px" }}>
                      <div style={{ fontSize: "13px", fontWeight: 700, color: "#111", marginBottom: "4px" }}>{s.title}</div>
                      <div style={{ fontSize: "12px", color: "#666", lineHeight: 1.5 }}>{s.desc}</div>
                    </div>
                  </>
                )}
              </div>
              {i < 5 && (
                <div style={{
                  width: "2px", height: "36px",
                  background: "linear-gradient(to bottom, rgba(123,25,188,0.5), rgba(123,25,188,0.1))",
                }} />
              )}
            </div>
          ))}
        </div>
      </section>

      {/* ── Timeline (dark) ── */}
      <section style={styles.sectionDark} aria-label="60-day project timeline">
        <div style={styles.eyebrow}>The Process</div>
        <h2 style={styles.h2}>60 days. Structured. No wasted motion.</h2>

        <div style={{ marginTop: "60px", position: "relative" }}>
          {[
            { week: "Week 1", title: "Discovery & Strategy", desc: "Deep-dive into the business: service area, pricing, competition, and what a perfect customer looks like." },
            { week: "Week 2", title: "Website Design & Build", desc: "Homepage, service pages, and neighbourhood landing pages go live. Mobile-optimised and built to convert." },
            { week: "Weeks 3–4", title: "CRM Setup & Automation", desc: "Pipeline stages created, lead forms connected, instant-response automations activated and follow-up sequences loaded." },
            { week: "Week 5", title: "Local SEO & Listings", desc: "Google Business Profile optimised. 15+ directory listings created. Neighbourhood pages indexed." },
            { week: "Weeks 6–8", title: "Launch, Optimise & Hand Over", desc: "Go live, monitor lead flow, tweak conversion points, and hand over a dashboard the owner can actually use." },
          ].map((item, i) => {
            const isLeft = i % 2 === 0;
            return (
              <div key={item.week} style={{ display: "flex", flexDirection: "column", alignItems: isLeft ? "flex-start" : "flex-end", marginBottom: "0" }}>
                <div style={{ display: "flex", alignItems: "center", width: "100%", position: "relative" }}>
                  <div style={{ flex: 1, display: "flex", justifyContent: "flex-end", paddingRight: "24px" }}>
                    {!isLeft && (
                      <div style={{ background: "#161616", border: "1px solid #2a2a2a", borderRadius: "4px", padding: "20px 24px", maxWidth: "320px", textAlign: "right" }}>
                        <div style={{ fontSize: "11px", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#7b19bc", marginBottom: "4px" }}>{item.week}</div>
                        <div style={{ fontSize: "15px", fontWeight: 700, color: "#e0e0e0", marginBottom: "6px" }}>{item.title}</div>
                        <div style={{ fontSize: "13px", color: "#888", lineHeight: 1.6 }}>{item.desc}</div>
                      </div>
                    )}
                  </div>
                  <div style={{ width: "16px", height: "16px", borderRadius: "50%", background: "#7b19bc", border: "2px solid #0F0F0F", boxShadow: "0 0 0 3px rgba(123,25,188,0.25)", flexShrink: 0, zIndex: 1 }} />
                  <div style={{ flex: 1, paddingLeft: "24px" }}>
                    {isLeft && (
                      <div style={{ background: "#161616", border: "1px solid #2a2a2a", borderRadius: "4px", padding: "20px 24px", maxWidth: "320px" }}>
                        <div style={{ fontSize: "11px", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#7b19bc", marginBottom: "4px" }}>{item.week}</div>
                        <div style={{ fontSize: "15px", fontWeight: 700, color: "#e0e0e0", marginBottom: "6px" }}>{item.title}</div>
                        <div style={{ fontSize: "13px", color: "#888", lineHeight: 1.6 }}>{item.desc}</div>
                      </div>
                    )}
                  </div>
                </div>
                {i < 4 && (
                  <div style={{ width: "50%", display: "flex", justifyContent: isLeft ? "flex-end" : "flex-start", alignSelf: "stretch" }}>
                    <div style={{
                      borderRight: isLeft ? "2px solid rgba(123,25,188,0.35)" : "none",
                      borderLeft: !isLeft ? "2px solid rgba(123,25,188,0.35)" : "none",
                      borderBottom: "2px solid rgba(123,25,188,0.35)",
                      borderRadius: isLeft ? "0 0 12px 0" : "0 0 0 12px",
                      width: "50%",
                      height: "48px",
                      marginRight: isLeft ? "-1px" : "0",
                      marginLeft: !isLeft ? "-1px" : "0",
                    }} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* ── Results (light) ── */}
      <section style={styles.sectionLight} aria-label="Results achieved for Toronto Junk Disposal">
        <div style={styles.eyebrowDark}>The Results</div>
        <h2 style={styles.h2Dark}>Numbers that changed how the business runs.</h2>

        <div style={styles.resultsGrid}>
          <div style={styles.resultCardHighlight}>
            <div style={styles.resultNum}>40+</div>
            <div style={styles.resultLabel}>Inbound leads in the first 60 days</div>
            <div style={styles.resultDesc}>
              Before Reflex, the business relied entirely on word-of-mouth. After
              launch, inbound enquiries came through the website consistently
              every week without any paid advertising.
            </div>
          </div>
          <div style={styles.resultCard}>
            <div style={styles.resultNum}>&lt;2 min</div>
            <div style={styles.resultLabel}>Average lead response time</div>
            <div style={styles.resultDesc}>
              Previously 24–48 hours. Automated responses now confirm every
              enquiry within 60–120 seconds, dramatically increasing the
              chance of booking before a competitor responds.
            </div>
          </div>
          <div style={styles.resultCard}>
            <div style={styles.resultNum}>3x</div>
            <div style={styles.resultLabel}>Monthly quote volume increase</div>
            <div style={styles.resultDesc}>
              Quote requests tripled compared to the pre-Reflex baseline,
              driven by the website's conversion structure and the neighbourhood
              SEO pages drawing targeted local traffic.
            </div>
          </div>
          <div style={styles.resultCardHighlight}>
            <div style={styles.resultNum}>100%</div>
            <div style={styles.resultLabel}>Leads captured, none lost to chaos</div>
            <div style={styles.resultDesc}>
              Every enquiry now enters the CRM automatically. No more missed
              WhatsApp messages, lost phone numbers, or forgotten follow-ups.
              The pipeline is always accurate and always visible.
            </div>
          </div>
        </div>
      </section>

      {/* ── Testimonial (dark) ── */}
      <div style={styles.testimonialWrap}>
        <span style={styles.quoteIcon}>"</span>
        <p style={styles.quoteText}>
          Before Reflex, I was doing everything in my head — calls, texts,
          trying to remember who asked for a quote two weeks ago. Now I open
          one screen and I can see every lead, every job, every follow-up that's
          gone out. The website alone brought in more enquiries in the first
          month than I had in the six months before.
        </p>
        <div style={styles.quoteAuthor}>
          <div style={{ ...styles.quoteAvatar, overflow: "hidden", padding: 0 }}>
            <img
              src="https://pub-b515366b2bdd4fb696b694c901df278b.r2.dev/images/62cfdef7-3241-4433-b0cf-89e4658a6c43.jfif"
              alt="Dmitri O., Owner of Toronto Junk Disposal"
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          </div>
          <div>
            <div style={styles.quoteAuthorName}>Dmitri O.</div>
            <div style={styles.quoteAuthorTitle}>
              Owner — Toronto Junk Disposal · Toronto, ON
            </div>
            <a
              href="https://torontjunkdisposal.online/"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: "inline-block",
                marginTop: "8px",
                fontSize: "12px",
                fontWeight: 600,
                color: "#7b19bc",
                textDecoration: "none",
                border: "1px solid rgba(123,25,188,0.35)",
                borderRadius: "4px",
                padding: "4px 12px",
                letterSpacing: "0.04em",
              }}
              onMouseEnter={(e) => (e.target.style.background = "rgba(123,25,188,0.12)")}
              onMouseLeave={(e) => (e.target.style.background = "transparent")}
            >
              Visit Website →
            </a>
          </div>
        </div>
      </div>

      {/* ── CTA (dark) ── */}
      <section style={styles.ctaSection} aria-label="Book a free strategy call with Reflex Advisor">
        <h2 style={{ ...styles.h2, maxWidth: "none", textAlign: "center", margin: "0 auto 20px auto" }}>
          Ready to grow your{" "}
          <span style={styles.heroOrange}>service business?</span>
        </h2>
        <p style={styles.ctaSub}>
          We work with home service companies across Canada — junk removal,
          landscaping, cleaning, moving, and more. Let's build your growth
          engine.
        </p>
        <a
          href="https://www.reflexadvisor.com"
          style={styles.ctaBtn}
          onMouseEnter={(e) => {
            e.target.style.background = "#6512a0";
            e.target.style.transform = "translateY(-1px)";
          }}
          onMouseLeave={(e) => {
            e.target.style.background = "#7b19bc";
            e.target.style.transform = "translateY(0)";
          }}
        >
          Book a Free Strategy Call →
        </a>
      </section>

      {/* ── Footer strip ── */}
      <footer style={styles.footerStrip} aria-label="Page footer">
        <span>© {new Date().getFullYear()} Reflex Advisor — reflexadvisor.com</span>
        <span>Case Study: Toronto Junk Disposal</span>
      </footer>

    </div>
  );
}